package com.schoolconnect.app.feature.imports

import com.schoolconnect.app.downloadFile
import korlibs.io.compression.CompressionMethod
import korlibs.io.compression.zip.ZipBuilder
import korlibs.io.file.std.cacheVfs
import korlibs.io.file.deleteRecursively

private const val XlsxMimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

/** Creates a small, standards-compliant XLSX workbook containing validation errors. */
suspend fun downloadValidationErrorWorkbook(
    fileName: String,
    rows: List<ImportErrorRow>
): Result<Unit> = runCatching {
    require(rows.isNotEmpty()) { "There are no validation errors to download." }

    val folder = cacheVfs["validation-error-export-${fileName.hashCode().toUInt()}-${rows.size}"]
    folder.mkdirs()
    try {
        folder["_rels"].mkdirs()
        folder["xl"].mkdirs()
        folder["xl/_rels"].mkdirs()
        folder["xl/worksheets"].mkdirs()
        val files = mapOf(
            "[Content_Types].xml" to contentTypesXml,
            "_rels/.rels" to rootRelationshipsXml,
            "xl/workbook.xml" to workbookXml,
            "xl/_rels/workbook.xml.rels" to workbookRelationshipsXml,
            "xl/styles.xml" to stylesXml,
            "xl/worksheets/sheet1.xml" to worksheetXml(rows)
        )
        files.forEach { (path, content) ->
            folder[path].writeBytes(content.encodeToByteArray())
        }
        val bytes = ZipBuilder.createZipFromTree(folder, CompressionMethod.Uncompressed, useFolderAsRoot = true)
        check(downloadFile(fileName.ensureXlsxExtension(), bytes, XlsxMimeType)) {
            "File downloads are not supported on this platform."
        }
    } finally {
        folder.deleteRecursively()
    }
}

private fun String.ensureXlsxExtension(): String =
    if (endsWith(".xlsx", ignoreCase = true)) this else "$this.xlsx"

private fun xmlEscape(value: String): String = value
    .replace("&", "&amp;")
    .replace("<", "&lt;")
    .replace(">", "&gt;")
    .replace("\"", "&quot;")
    .replace("'", "&apos;")

private fun worksheetXml(rows: List<ImportErrorRow>): String {
    val allRows = buildString {
        append("<row r=\"1\">")
        listOf("Sheet Name", "Row No.", "Status", "Error Message").forEachIndexed { index, value ->
            append(inlineCell(columnName(index + 1) + "1", value, style = 1))
        }
        append("</row>")
        rows.forEachIndexed { index, row ->
            val rowNumber = index + 2
            append("<row r=\"$rowNumber\">")
            append(inlineCell("A$rowNumber", row.sheetName))
            append(inlineCell("B$rowNumber", row.rowNo))
            append(inlineCell("C$rowNumber", "Error", style = 2))
            append(inlineCell("D$rowNumber", row.message))
            append("</row>")
        }
    }
    val lastRow = rows.size + 1
    return """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:D$lastRow"/>
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <sheetFormatPr defaultRowHeight="20"/>
  <cols><col min="1" max="1" width="20" customWidth="1"/><col min="2" max="2" width="12" customWidth="1"/><col min="3" max="3" width="14" customWidth="1"/><col min="4" max="4" width="75" customWidth="1"/></cols>
  <sheetData>$allRows</sheetData>
  <autoFilter ref="A1:D$lastRow"/>
</worksheet>"""
}

private fun columnName(number: Int): String = when (number) {
    1 -> "A"
    2 -> "B"
    3 -> "C"
    else -> "D"
}

private fun inlineCell(reference: String, value: String, style: Int? = null): String {
    val styleAttribute = style?.let { " s=\"$it\"" }.orEmpty()
    return "<c r=\"$reference\" t=\"inlineStr\"$styleAttribute><is><t>${xmlEscape(value)}</t></is></c>"
}

private val contentTypesXml = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>"""

private val rootRelationshipsXml = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>"""

private val workbookXml = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><bookViews><workbookView/></bookViews><sheets><sheet name="Validation Errors" sheetId="1" r:id="rId1"/></sheets></workbook>"""

private val workbookRelationshipsXml = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>"""

private val stylesXml = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFDCE6F1"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" applyFont="1" applyFill="1"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" applyFont="1"/></cellXfs></styleSheet>"""
